import {
  type Contenido,
  formatearRevision,
  NOMBRE_ESTADO,
  NOMBRE_TIPO_CORRECCION,
  seccionesFaltantes,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Editor as EditorTipTap } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { queryClient, trpcClient, useTRPC } from "../api.ts";
import { BotonPdf } from "../componentes/BotonPdf.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { ItemDeMarca } from "../componentes/PanelDeMarcas.tsx";
import { Aviso, Boton, BotonEnlace, EstadoVacio } from "../componentes/ui.tsx";
import { type EstadoGuardado, useAutoguardado, useHaceCuanto } from "../editor/autoguardado.ts";
import { Editor, type SubirImagen, useEditorDeContenido } from "../editor/Editor.tsx";
import { irAMarca, sincronizarMarcas } from "../editor/marcas.ts";
import { useFlujogramas } from "../editor/mermaid.ts";
import { contenidoDesdeEditor } from "../editor/nodos.ts";
import { datosDeHoja } from "../editor/VistaDeContenido.tsx";
import { VistaHoja } from "../editor/VistaHoja.tsx";
import type { Obtenido } from "../tipos.ts";

/**
 * Pantalla 05 · Redactar documento. Tres columnas: secciones · editor · vista previa. La
 * vista previa es la misma hoja que imprime el worker, con lo que se está escribiendo. Se
 * abre sobre la revisión en curso del documento; sin revisión en curso, sobre la principal, de
 * solo lectura.
 */
export function PaginaRedactar() {
  const { codigo } = useParams({ from: "/app/documentos/$codigo/editar" });
  const trpc = useTRPC();
  const detalle = useQuery(trpc.documentos.detalle.queryOptions({ codigo }));
  const revisionId = detalle.data?.revisionEnCursoId ?? detalle.data?.principalId ?? null;
  const obtenido = useQuery({
    ...trpc.contenido.obtener.queryOptions({ revisionId: revisionId ?? "" }),
    enabled: revisionId !== null,
    // Lo que el editor tiene es la verdad mientras se edita: no se vuelve a pedir solo. Al
    // salir no queda en caché: reabrir con el contenido y el hash viejos parece que se perdió
    // lo escrito y el primer guardado choca.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  if (detalle.isPending || (revisionId && obtenido.isPending)) {
    return <EstadoVacio>Cargando…</EstadoVacio>;
  }
  if (detalle.isError) return <EstadoVacio>{detalle.error.message}</EstadoVacio>;
  if (!revisionId) return <EstadoVacio>Este documento no tiene ninguna revisión.</EstadoVacio>;
  if (obtenido.isError) return <EstadoVacio>{obtenido.error.message}</EstadoVacio>;
  if (!obtenido.data) return null;
  return <Redactor key={revisionId} codigo={codigo} inicial={obtenido.data} />;
}

function Redactor({ codigo, inicial }: { codigo: string; inicial: Obtenido }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const doc = inicial.documento;
  const rev = inicial.revision;
  const editable = inicial.puedeEditar;
  const [contenido, setContenido] = useState<Contenido>(inicial.contenido);
  const [aviso, setAviso] = useState<string | null>(null);

  const guardado = useAutoguardado({
    revisionId: rev.id,
    activo: editable,
    hashInicial: rev.hashContenido,
    guardadoInicial: rev.contenidoGuardadoEn,
  });

  // La vista previa se redibuja un momento después de la última tecla, no en cada una.
  const temporizadorVista = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alCambiar = useCallback(
    (editor: EditorTipTap) => {
      guardado.marcarCambio(editor);
      if (temporizadorVista.current) clearTimeout(temporizadorVista.current);
      temporizadorVista.current = setTimeout(() => {
        const r = contenidoDesdeEditor(editor.getJSON());
        if (r.ok) setContenido(r.contenido);
      }, 300);
    },
    [guardado.marcarCambio],
  );
  useEffect(
    () => () => {
      if (temporizadorVista.current) clearTimeout(temporizadorVista.current);
    },
    [],
  );

  const editor = useEditorDeContenido({
    tipoCodigo: doc.tipo.codigo,
    contenido: inicial.contenido,
    editable,
    onCambio: alCambiar,
  });

  const subir = useMutation(trpc.contenido.subirImagen.mutationOptions());
  const publicar = useMutation(trpc.documentos.publicar.mutationOptions());
  const informativo = !doc.tipo.requiereAprobacion;
  const subirImagen: SubirImagen = (archivo) => subir.mutateAsync({ revisionId: rev.id, archivo });

  const faltantes = useMemo(
    () => seccionesFaltantes(contenido, doc.tipo.codigo),
    [contenido, doc.tipo.codigo],
  );
  const svgs = useFlujogramas(contenido);

  // Fase 4: las marcas del revisor. Las abiertas van resaltadas en el editor y siguen al texto.
  const marcasQ = useQuery(trpc.correcciones.listar.queryOptions({ revisionId: rev.id }));
  const marcas = marcasQ.data?.marcas ?? [];
  const abiertas = marcas.filter((m) => m.estado === "abierta");
  const [marcaActiva, setMarcaActiva] = useState<number | null>(null);
  useEffect(() => {
    if (!editor || !marcasQ.data) return;
    sincronizarMarcas(
      editor,
      marcasQ.data.marcas.flatMap((m) => {
        if (m.estado !== "abierta" || !m.ancla) return [];
        const titulo = `${NOMBRE_TIPO_CORRECCION[m.tipo]} ${m.numero} · ${m.autor.nombre}\n${m.comentario}`;
        // El primer tramo lleva el número; los demás (una marca sobre varios párrafos), solo el color.
        return [{ ancla: m.ancla, lugar: m.lugar }, ...(m.tramos ?? [])].flatMap((t, i) =>
          t.lugar
            ? [
                {
                  numero: m.numero,
                  tipo: m.tipo,
                  bloqueId: t.ancla.bloqueId,
                  inicio: t.lugar.inicio,
                  fin: t.lugar.fin,
                  titulo,
                  sinNumero: i > 0,
                },
              ]
            : [],
        );
      }),
    );
  }, [editor, marcasQ.data]);
  const datos = useMemo(
    () => ({ ...datosDeHoja(inicial, contenido), svgs }),
    [inicial, contenido, svgs],
  );

  const [enviando, setEnviando] = useState(false);
  async function enviarARevision() {
    if (enviando) return;
    setEnviando(true);
    try {
      await enviarUnaVez();
    } finally {
      setEnviando(false);
    }
  }

  async function enviarUnaVez() {
    await guardado.guardarAhora();
    // Si lo último no llegó al servidor, se publicaría (o se firmaría) la versión anterior.
    if (!guardado.estaGuardado()) {
      setAviso(
        "No se pudo guardar lo último que escribiste. Revisa la conexión y vuelve a intentarlo; no se envió nada.",
      );
      return;
    }
    if (informativo) {
      // Fase 3b: sin ruta de firmas; se publica y listo.
      try {
        await publicar.mutateAsync({ revisionId: rev.id });
      } catch (e) {
        setAviso(e instanceof Error ? e.message : "No se pudo publicar");
        return;
      }
      queryClient.invalidateQueries();
      navigate({ to: "/documentos/$codigo", params: { codigo }, search: {} });
      return;
    }
    queryClient.invalidateQueries();
    navigate({ to: "/documentos/$codigo", params: { codigo }, search: { firmar: true } });
  }

  function irASeccion(id: string) {
    if (!editor) return;
    let pos: number | null = null;
    editor.state.doc.forEach((n, offset) => {
      if (n.attrs.id === id) pos = offset;
    });
    if (pos === null) return;
    // pos es la sección; +1 es el inicio de su contenido y +2 el del primer bloque.
    editor
      .chain()
      .focus(pos + 2)
      .run();
    const el = editor.view.nodeDOM(pos) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const bloqueado = guardado.estado.estado === "conflicto";
  const listoParaEnviar =
    editable && faltantes.length === 0 && !bloqueado && rev.estado === "borrador";

  return (
    <div>
      <Encabezado
        overline={`${doc.proceso.nombre} › Documentos › ${doc.codigo} · Rev. ${formatearRevision(rev.numero)} · ${NOMBRE_ESTADO[rev.estado]}`}
        titulo={doc.nombre}
        acciones={
          <>
            <IndicadorGuardado estado={guardado.estado} editable={editable} />
            <BotonEnlace to="/documentos/$codigo" params={{ codigo }}>
              Volver al documento
            </BotonEnlace>
            <BotonPdf
              etiqueta="Vista previa PDF"
              clave={["revision", rev.id]}
              consultar={() => vistaPdfDe(codigo, rev.id)}
              pedir={() => trpcClient.contenido.pedirVistaPdf.mutate({ revisionId: rev.id })}
              antes={editable ? guardado.guardarAhora : undefined}
              onAviso={setAviso}
            />
            {editable && (
              <Boton
                variante="firma"
                disabled={!listoParaEnviar || enviando}
                title={
                  listoParaEnviar
                    ? informativo
                      ? "Publicar sin ruta de firmas"
                      : doc.tipo.conRevisor
                        ? "Firmar como quien elabora y enviar a revisión"
                        : "Firmar como quien elabora y enviar a aprobación"
                    : "Completa las secciones obligatorias para enviar"
                }
                onClick={() => void enviarARevision()}
              >
                {informativo
                  ? "Publicar"
                  : doc.tipo.conRevisor
                    ? "Enviar a revisión"
                    : "Enviar a aprobación"}
              </Boton>
            )}
          </>
        }
      />

      {!editable && (
        <Aviso tono="ambar" className="mb-4">
          Solo lectura:{" "}
          {rev.estado === "borrador"
            ? "esta revisión la edita quien la elabora."
            : "esta revisión ya se envió; para cambiarla hay que devolverla a borrador."}
        </Aviso>
      )}
      {guardado.estado.estado === "conflicto" && (
        <Aviso className="mb-4">{guardado.estado.mensaje}</Aviso>
      )}
      {guardado.estado.estado === "error" && (
        <Aviso className="mb-4">No se pudo guardar: {guardado.estado.mensaje}</Aviso>
      )}
      {aviso && <Aviso className="mb-4">{aviso}</Aviso>}

      <div className="grid gap-5 xl:grid-cols-[210px_minmax(0,1fr)_minmax(320px,38%)]">
        <aside className="redactar-secciones">
          <div className="mb-2 text-[11px] font-semibold tracking-[.12em] text-texto-3 uppercase">
            Plantilla · {doc.tipo.nombre}
          </div>
          <ol className="space-y-0.5">
            {contenido.content.map((s, i) => {
              const def = inicial.plantilla.find((p) => p.clave === s.attrs.clave);
              const falta = faltantes.some((f) => f.clave === s.attrs.clave);
              return (
                <li key={s.attrs.id}>
                  <button
                    type="button"
                    onClick={() => irASeccion(s.attrs.id)}
                    className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left text-[12.5px] hover:bg-marino-claro"
                  >
                    <span className="w-5 shrink-0 text-right font-mono text-texto-3">{i + 1}.</span>
                    <span className="min-w-0 flex-1 truncate">{s.attrs.titulo}</span>
                    {falta ? (
                      <span className="shrink-0 rounded bg-ambar-fondo px-1.5 text-[10px] font-semibold text-ambar">
                        falta
                      </span>
                    ) : def?.automatica ? (
                      <span className="shrink-0 text-[10px] text-texto-3">auto</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="mt-4 text-[11.5px] text-texto-3">
            <b>Encabezado y pie.</b> Logo, código, revisión, fecha y "Página X de Y" los pone el
            sistema. No hay que tocarlos.
          </p>
        </aside>

        <section className="min-w-0">
          {editor && (
            <Editor
              editor={editor}
              contexto={{
                tipoCodigo: doc.tipo.codigo,
                plantilla: inicial.plantilla,
                faltantes: faltantes.map((f) => f.clave),
                editable: editable && !bloqueado,
              }}
              subirImagen={subirImagen}
              onAviso={setAviso}
            />
          )}
          <BarraDeEstado
            faltantes={faltantes.map((f) => f.titulo)}
            editable={editable}
            informativo={informativo}
          />
        </section>

        <aside className="redactar-vista">
          {marcas.length > 0 && (
            <div className="mb-4 rounded-tarjeta border border-borde bg-white">
              <div className="flex items-baseline justify-between border-b border-borde px-4 py-2.5">
                <span className="font-display text-[14px] font-bold text-marino">
                  Marcas del revisor
                </span>
                <span className="text-[12px] text-texto-3">
                  {abiertas.length
                    ? `${abiertas.length} abierta${abiertas.length === 1 ? "" : "s"}`
                    : "todas resueltas"}
                </span>
              </div>
              <ol className="max-h-[420px] divide-y divide-borde/60 overflow-y-auto">
                {marcas.map((m) => (
                  <ItemDeMarca
                    key={m.id}
                    marca={m}
                    activa={m.numero === marcaActiva}
                    onElegir={() => {
                      setMarcaActiva(m.numero);
                      if (editor && !irAMarca(editor, m.numero) && m.estado === "abierta") {
                        setAviso(`La frase de la marca ${m.numero} ya no está en el texto.`);
                      }
                    }}
                    onAviso={setAviso}
                  />
                ))}
              </ol>
            </div>
          )}
          <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold tracking-[.12em] text-texto-3 uppercase">
            <span>Vista previa</span>
            <span className="normal-case tracking-normal">se actualiza sola</span>
          </div>
          <VistaHoja datos={datos} />
        </aside>
      </div>
    </div>
  );
}

/**
 * "Vista previa PDF": el mismo PDF que imprime el worker, con la marca de agua BORRADOR. Si ya
 * existe para este contenido aparece el enlace; si no, el worker la hace. Se pregunta por el
 * detalle del documento: ahí llega la vista previa de la revisión, o el fallo del trabajo.
 */
async function vistaPdfDe(codigo: string, revisionId: string) {
  const d = await trpcClient.documentos.detalle.query({ codigo });
  const revision = d.revisiones.find((r) => r.id === revisionId);
  return {
    archivoId: revision?.pdfVista?.id ?? null,
    fallo: d.trabajoFallido?.que === "vista" ? d.trabajoFallido : null,
  };
}

function IndicadorGuardado({ estado, editable }: { estado: EstadoGuardado; editable: boolean }) {
  const hace = useHaceCuanto(estado.guardadoEn);
  if (!editable) return null;
  const texto =
    estado.estado === "guardando"
      ? "Guardando…"
      : estado.estado === "pendiente"
        ? "Cambios sin guardar"
        : estado.estado === "error"
          ? "Sin guardar"
          : estado.estado === "conflicto"
            ? "Guardado detenido"
            : estado.guardadoEn
              ? `Guardado ${hace}`
              : "Sin cambios";
  const tono =
    estado.estado === "error" || estado.estado === "conflicto"
      ? "text-rojo"
      : estado.estado === "guardado"
        ? "text-verde"
        : "text-texto-3";
  return (
    <span className={`self-center px-2 text-[12px] font-semibold ${tono}`} aria-live="polite">
      {texto}
    </span>
  );
}

function BarraDeEstado({
  faltantes,
  editable,
  informativo,
}: {
  faltantes: string[];
  editable: boolean;
  informativo: boolean;
}) {
  const meta = informativo ? "publicar" : "enviar a revisión";
  return (
    <div
      className={`mt-3 rounded-md border px-4 py-2.5 text-[12.5px] ${
        faltantes.length
          ? "border-ambar/40 bg-ambar-fondo text-ambar"
          : "border-verde/40 bg-verde-fondo text-verde"
      }`}
      aria-live="polite"
    >
      {faltantes.length ? (
        <>
          <b>
            {faltantes.length === 1 ? "Falta 1 sección" : `Faltan ${faltantes.length} secciones`}
          </b>{" "}
          para poder {meta}: {faltantes.join(", ")}.
        </>
      ) : (
        <>
          <b>Plantilla completa.</b>{" "}
          {editable ? `Puedes ${meta}.` : "Las secciones obligatorias están dichas."}
        </>
      )}
    </div>
  );
}
